// ShopifyBridge — postMessage wrapper for the iframed wizard.
//
// The wizard is hosted at syhouse.smartify.pt and embedded in
// smartify.pt/pages/syhouse via <iframe>. This module:
//   1) Detects standalone vs iframed mode.
//   2) Provides a typed postMessage helper for parent ↔ child events
//      (cart hand-off, height resize, lang change).
//
// Phase 0: scaffolding only — Phase 4 wires real cart hand-off events
// when /config/finalize returns Shopify checkoutUrl.

export type BridgeMode = 'standalone' | 'iframe';

export type SYHouseEvent =
  | { type: 'syhouse:ready'; version: string }
  | { type: 'syhouse:resize'; height: number }
  | { type: 'syhouse:lang'; lang: 'pt' | 'en' | 'es' }
  | { type: 'syhouse:cart-handoff'; checkoutUrl: string };

const TRUSTED_PARENTS = [
  'https://smartify.pt',
  'https://www.smartify.pt',
  'https://house.smartify.pt',
  'https://syhouse.smartify.pt',
];

export function detectMode(): BridgeMode {
  try {
    return window.self !== window.top ? 'iframe' : 'standalone';
  } catch {
    // Cross-origin access throws → we are iframed.
    return 'iframe';
  }
}

const EMBED_FLAG = 'syhouse:embed';

// Embed mode = the wizard is rendered as the Shopify-embedded experience.
// Triggered by the iframe src carrying `?source=shopify-embed`. The flag is
// sticky for the tab (sessionStorage) because the SPA navigates client-side
// and the query string is lost after the first route change. A raw iframe
// (no marker, e.g. a future partner embed) also counts as embed.
export function isEmbedMode(): boolean {
  try {
    const src = new URLSearchParams(window.location.search).get('source');
    if (src === 'shopify-embed') {
      sessionStorage.setItem(EMBED_FLAG, '1');
      return true;
    }
    if (sessionStorage.getItem(EMBED_FLAG) === '1') return true;
  } catch {
    /* sessionStorage may be unavailable in some embeds — fall through */
  }
  return detectMode() === 'iframe';
}

// Report the document height to the parent so the Shopify <iframe> can grow
// to fit the wizard instead of trapping it in a fixed-height inner scroll.
// Returns a cleanup fn. No-op in standalone mode.
export function startAutoResize(): () => void {
  if (detectMode() === 'standalone') return () => {};
  let raf = 0;
  const send = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const height = Math.ceil(
        Math.max(
          document.documentElement.scrollHeight,
          document.body?.scrollHeight ?? 0,
        ),
      );
      if (height > 0) emit({ type: 'syhouse:resize', height });
    });
  };
  const ro = new ResizeObserver(send);
  ro.observe(document.documentElement);
  if (document.body) ro.observe(document.body);
  window.addEventListener('load', send);
  send();
  return () => {
    cancelAnimationFrame(raf);
    ro.disconnect();
    window.removeEventListener('load', send);
  };
}

export function emit(ev: SYHouseEvent): void {
  if (detectMode() === 'standalone') return;
  // Send to all trusted parents; the actual one will accept, others ignore.
  for (const origin of TRUSTED_PARENTS) {
    try {
      window.parent.postMessage(ev, origin);
    } catch {
      /* swallow — origin mismatch is expected */
    }
  }
}

export function onParentMessage(
  handler: (ev: SYHouseEvent) => void,
): () => void {
  const listener = (msg: MessageEvent) => {
    if (!TRUSTED_PARENTS.includes(msg.origin)) return;
    const data = msg.data as SYHouseEvent | undefined;
    if (!data || typeof data !== 'object' || !('type' in data)) return;
    if (typeof data.type === 'string' && data.type.startsWith('syhouse:')) {
      handler(data);
    }
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
}
